import { Component, Inject, OnInit, Optional } from '@angular/core';
import { AssetInput, JsonLdObject } from "@think-it-labs/edc-connector-client";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { StorageType } from "../../models/storage-type";
import { MatTabChangeEvent } from '@angular/material/tabs';
import { getMediaTypes } from 'src/modules/management-api-client';
import { OAuth2AssetProps } from '../../models/oauth2-asset-props';
import { MANAGEMENT_API_CONTEXT } from 'src/modules/management-api-client/variables';

export interface AssetEditorDialogData {
  asset?: any;
  agreementCount?: number;
}

@Component({
  selector: 'edc-demo-asset-editor-dialog',
  templateUrl: './asset-editor-dialog.component.html',
  styleUrls: ['./asset-editor-dialog.component.scss']
})
export class AssetEditorDialog implements OnInit {

  editMode: boolean = false;
  agreementCount: number = 0;
  selectedAuthTabIndex: number = 0;

  id: string = '';
  version: string = '';
  name: string = '';

  baseUrl: string = '';

  authToken: string = '';
  apiKey: string = '';

  isProxyPath: boolean = true;
  isProxyQueryParams: boolean = true;

  authorizationTypeTab: string = 'Authorization Token';
  staticHeaders: { key: string; value: string }[] = [];
  metadataEntries: JsonLdObject = {} as JsonLdObject;

  isDisplayBaseUrl: boolean = true;

  mediaTypes: string[] = getMediaTypes(); // Get all available media types
  contenttype: string = 'application/json'; // Default selected content type
  isOtherSelected: boolean = false; // Flag to show/hide custom input

  iamClientId: string = '';
  iamClientSecret: string = '';
  iamTokenUrl: string = '';
  iamScope: string = '';
  iamGrantType: string = 'client_credentials';
  iamUser: string = '';
  iamPassword: string = '';

  grantTypes: string[] = ['client_credentials', 'password']

  // properties/dataAddress entries not represented by the form; preserved on save in edit mode
  private extraProperties: Record<string, any> = {};
  private extraDataAddressProps: Record<string, any> = {};

  constructor(
    private dialogRef: MatDialogRef<AssetEditorDialog>,
    @Inject('STORAGE_TYPES') public storageTypes: StorageType[],
    @Optional() @Inject(MAT_DIALOG_DATA) private readonly data: AssetEditorDialogData | null
  ) {
    this.mediaTypes = getMediaTypes();
  }

  ngOnInit(): void {
    if (this.data?.asset) {
      this.editMode = true;
      this.agreementCount = this.data.agreementCount ?? 0;
      this.prefillFromAsset(this.data.asset);
    }
  }

  private prefillFromAsset(asset: any): void {
    const properties = this.toPlainRecord(asset.properties);
    const dataAddress = this.toPlainRecord(asset.dataAddress);

    this.id = asset.id ?? asset['@id'] ?? '';
    this.name = properties['name'] ?? '';
    this.version = properties['version'] ?? '';
    this.contenttype = properties['contenttype'] ?? dataAddress['contentType'] ?? '';
    this.isProxyPath = this.toBoolean(properties['proxyPath'] ?? dataAddress['proxyPath'], true);
    this.isProxyQueryParams = this.toBoolean(properties['proxyQueryParams'] ?? dataAddress['proxyQueryParams'], true);
    this.baseUrl = dataAddress['baseUrl'] ?? properties['baseUrl'] ?? '';
    this.isDisplayBaseUrl = properties['baseUrl'] !== undefined && properties['baseUrl'] !== null;
    this.metadataEntries = (properties['metadata'] ?? {}) as JsonLdObject;

    if (this.contenttype && !this.mediaTypes.includes(this.contenttype)) {
      this.isOtherSelected = true;
    }

    const headers = Object.keys(dataAddress)
      .filter(key => key.startsWith('header:'))
      .map(key => ({ key: key.substring('header:'.length), value: `${dataAddress[key]}` }));

    // content-type is re-added automatically on save
    const consumedHeaderKeys = new Set<string>(['content-type']);
    const authHeader = headers.find(header => header.key.toLowerCase() === 'authorization');
    const apiKeyHeader = headers.find(header => header.key.toLowerCase() === 'x-api-key');

    if (dataAddress['oauth2:tokenUrl']) {
      this.setAuthTab('OAuth 2.0');
      this.iamTokenUrl = dataAddress['oauth2:tokenUrl'] ?? '';
      this.iamClientId = dataAddress['oauth2:clientId'] ?? '';
      this.iamClientSecret = dataAddress['oauth2:clientSecret'] ?? '';
      this.iamScope = dataAddress['oauth2:scope'] ?? '';
      this.iamGrantType = dataAddress['oauth2:grantType'] ?? 'client_credentials';
      this.iamUser = dataAddress['oauth2:username'] ?? '';
      this.iamPassword = dataAddress['oauth2:password'] ?? '';
    } else if (apiKeyHeader) {
      this.setAuthTab('API Key');
      this.apiKey = apiKeyHeader.value;
      consumedHeaderKeys.add('x-api-key');
    } else if (authHeader) {
      this.setAuthTab('Authorization Token');
      this.authToken = authHeader.value;
      consumedHeaderKeys.add('authorization');
    }

    this.staticHeaders = headers.filter(header => !consumedHeaderKeys.has(header.key.toLowerCase()));

    const knownPropertyKeys = ['id', 'name', 'version', 'contenttype', 'proxyPath', 'proxyQueryParams', 'metadata', 'baseUrl'];
    this.extraProperties = this.omitKeys(properties, key => knownPropertyKeys.includes(key));

    const knownDataAddressKeys = ['@context', '@type', 'type', 'baseUrl', 'proxyPath', 'proxyQueryParams', 'contentType'];
    this.extraDataAddressProps = this.omitKeys(dataAddress, key =>
      knownDataAddressKeys.includes(key) || key.startsWith('header:') || key.startsWith('oauth2:'));
  }

  private setAuthTab(label: string): void {
    this.authorizationTypeTab = label;
    this.selectedAuthTabIndex = ['Authorization Token', 'API Key', 'OAuth 2.0'].indexOf(label);
  }

  private toPlainRecord(obj: any): Record<string, any> {
    const record: Record<string, any> = { ...(obj ?? {}) };
    delete record['optionalValue'];
    return record;
  }

  private omitKeys(record: Record<string, any>, shouldOmit: (key: string) => boolean): Record<string, any> {
    return Object.fromEntries(Object.entries(record).filter(([key]) => !shouldOmit(key)));
  }

  private toBoolean(value: any, defaultValue: boolean): boolean {
    if (value === undefined || value === null || value === '') return defaultValue;
    if (typeof value === 'boolean') return value;
    return `${value}`.toLowerCase() === 'true';
  }

  getPublicProperties() {
    const properties: any = {
      "name": this.name,
      "version": this.version,
      "contenttype": this.contenttype,
      "proxyPath": this.isProxyPath.toString(),
      "proxyQueryParams": this.isProxyQueryParams.toString(),
      "metadata": this.metadataEntries,
    };

    if (this.isDisplayBaseUrl) {
      properties.baseUrl = this.baseUrl;
    }

    return properties;
  }

  onSave() {
    
    this.baseUrl = this.getWithoutTrailingSlashIfExists(this.baseUrl);

    const publicProperties = this.getPublicProperties();
    const dataAddress = this.getDataAddress();

    const assetInput: AssetInput = {
      "@id": this.id,
      properties: publicProperties,
      dataAddress: dataAddress,
    };
    this.dialogRef.close({ assetInput });
  }

  onContentTypeChange(selectedValue: string): void {
    this.isOtherSelected = selectedValue === 'other';
    if (!this.isOtherSelected) {
      this.contenttype = selectedValue; // Update content type for predefined options
    }
  }

  onJsonLdUpdated(updatedJson: any) {
    this.metadataEntries = updatedJson;
  }

  getDataAddress() {
    if (this.authToken != "" || this.apiKey != "") {
      if (this.authorizationTypeTab == "Authorization Token") {
        this.addOrReplaceHeader("authorization", this.authToken);
      }
      else if (this.authorizationTypeTab == "API Key") {
        this.addOrReplaceHeader("x-api-key", this.apiKey);
      }
    }

    if (this.contenttype != "") {
      this.addOrReplaceHeader("content-type", this.contenttype);
    }

    const dataAddress = {
      "@context": [
        MANAGEMENT_API_CONTEXT,
      ],
      "type": "HttpData",
      "baseUrl": this.baseUrl,
      "proxyPath": this.isProxyPath.toString(),
      "proxyQueryParams": this.isProxyQueryParams.toString(),
      "contentType": this.contenttype,
    }

    const resultDataAddress = {
      ...this.extraDataAddressProps,
      ...dataAddress,
      ...this.getHeaderProps(),
      ...(this.authorizationTypeTab == "OAuth 2.0" ? this.getOAuth2Props(): {})
    };

    return resultDataAddress;
  }

  getHeaderProps() {
    return this.staticHeaders.reduce((acc, { key, value }) => {
      acc[`header:${key}`] = value;
      return acc;
    }, {} as any)
  }

  getOAuth2Props() {
    const oauthProps: OAuth2AssetProps = {
      "oauth2:tokenUrl": this.iamTokenUrl,
      "oauth2:clientId": this.iamClientId,
      "oauth2:clientSecret": this.iamClientSecret,
      "oauth2:grantType": this.iamGrantType,
      "oauth2:username": this.iamUser == '' ? undefined : this.iamUser,
      "oauth2:password": this.iamPassword == '' ? undefined : this.iamPassword,
      "oauth2:scope": this.iamScope == '' ? undefined : this.iamScope,
    };

    return oauthProps;
  }

  addOrReplaceHeader(headerKey: string, headerValue: string) {
    if (this.staticHeaders.some(x => x.key.toLowerCase() == headerKey)) {
      this.staticHeaders.forEach(x => {
        if (x.key.toLowerCase() == headerKey) {
          x.value = headerValue;
        }
      });
    } else {
      this.staticHeaders.push({key: headerKey, value: headerValue});
    }
  }

  addHeader() {
    this.staticHeaders.push({ key: '', value: '' });
  }

  removeHeader(index: number) {
    this.staticHeaders.splice(index, 1);
  }

  onAuthorizationTypeTabChange(event: MatTabChangeEvent) {
    this.authorizationTypeTab = event.tab.textLabel;
  }

  private getWithoutTrailingSlashIfExists(baseUrl: string) {
    let resultUrl: string;
    baseUrl = baseUrl.replace(/\s/g, "");

    if (baseUrl[baseUrl.length - 1] == '/') {
      resultUrl = baseUrl.slice(0, -1);
    } else {
      resultUrl = baseUrl;
    }

    return resultUrl;
  }
}
